import axios from "axios";
import { useCallback, useContext, useMemo } from "react";
import { TeamsFxContext } from "../components/teams/TeamsFxContext";
import { LocalStorageKeys } from "../types";
import { getAuth, User } from "firebase/auth";
import * as Sentry from "@sentry/react";
import { stubAdapter } from "../stubs/stub-adapter";
import { getDevBypassEmail, isDevAuthBypassEnabled } from "./devAuthBypass";

// Real Teams SSO doesn't work through a tunnel domain (the AAD app
// registration is pinned to a fixed production domain - see
// teams/README.md's "Authentication for a collaborator" section), and
// teamsUserCredential.getToken() hangs rather than rejecting when that
// happens. Without a timeout, every request would block forever waiting on
// a token that's never coming, instead of falling through to the dev
// bypass email below.
const TEAMS_TOKEN_TIMEOUT_MS = 5000;

const useGetTeamsToken = () => {
  const teamsUserCredential = useContext(TeamsFxContext).teamsUserCredential;

  return useCallback(async () => {
    if (!teamsUserCredential) {
      return undefined;
    }

    // Ignore errors for anonymous users
    try {
      const result = await Promise.race([
        teamsUserCredential.getToken(""),
        new Promise<null>((resolve) =>
          setTimeout(() => resolve(null), TEAMS_TOKEN_TIMEOUT_MS)
        ),
      ]);
      return result?.token;
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);
    }
  }, [teamsUserCredential]);
};

export const useAxios = () => {
  const getFirebaseToken = async (
    refresh?: boolean
  ): Promise<string | undefined> => {
    const auth = getAuth();
    const firebaseUser: User | null =
      auth.currentUser ||
      (await Promise.race([
        new Promise<User | null>((resolve) => {
          const unsubscribe = auth.onAuthStateChanged((currentUser) => {
            if (!localStorage.getItem(LocalStorageKeys.EXPECT_LOGIN)) {
              unsubscribe();
              console.debug("No firebase login expected...");
              return resolve(currentUser);
            }
            if (currentUser) {
              unsubscribe();
              console.debug("Firebase login expected, resolve current user...");
              return resolve(currentUser);
            }
          });
        }),
        // Under dev-bypass auth (Teams testing), Firebase is never actually
        // signed into - onAuthStateChanged only ever fires with a null
        // user, and EXPECT_LOGIN is set by a successful Teams login and
        // never cleared (see hooks/user.ts), so the condition above that
        // would resolve early never becomes true either. Without this,
        // every single request hangs here forever after the first
        // successful Teams login on this browser.
        new Promise<null>((resolve) => setTimeout(() => resolve(null), 5000)),
      ]));
    const token = firebaseUser?.getIdToken(refresh);
    return token;
  };

  const getTeamsToken = useGetTeamsToken();

  return useMemo(() => {
    // In stub mode, use the stub adapter so no real network requests are made
    if (import.meta.env.VITE_STUB) {
      return axios.create({ adapter: stubAdapter });
    }

    const instance = axios.create();

    // Set up the interceptor for this instance
    instance.interceptors.request.use(async (config) => {
      // VITE_DEV_AUTH_BYPASS is the single switch for every dev-auth-bypass
      // behavior on the frontend - real Teams SSO is skipped (see
      // TeamsRouter, which also uses this to leave teamsUserCredential
      // unset) and Firebase is never legitimately signed into either in
      // this mode, so getFirebaseToken() would just wait out its whole
      // timeout on every single request for nothing. getDevBypassEmail is
      // the actual auth mechanism used instead - only attempt it when this
      // is on, since it's meaningless otherwise (api-gateway ignores the
      // header it produces unless DEV_AUTH_BYPASS_EMAIL is set there too).
      const devAuthBypass = isDevAuthBypassEnabled();
      const token =
        (await getTeamsToken()) ||
        localStorage.getItem(LocalStorageKeys.LANGFINITY_TOKEN) ||
        (!devAuthBypass && (await getFirebaseToken()));

      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      if (devAuthBypass) {
        const devBypassEmail = await getDevBypassEmail();
        if (devBypassEmail && config.headers) {
          config.headers["X-Dev-Bypass-Email"] = devBypassEmail;
        }
      }

      return config;
    });

    return instance;
  }, [getTeamsToken]);
};
